import * as React from "react";
import { useSubscription } from "../../hooks/useSubscription";
import { loader } from "graphql.macro";
import { pipe, map } from "wonka";
import produce from "immer";
import { Trans, useTranslation } from "react-i18next";
import { useBehaviourSubject, useSource } from "../../hooks/useSource";
import { AbsoluteCenter } from "../AbsoluteCenter";
import { ButtonWithSound } from "../AudioProvider";
import styles from "../Area/TablesList/InstantTournaments.module.scss";
import classNames from "classnames";
import closeIcon from "../Area/TablesList/assets/close-icon.png";
import newRoundIcon from "./assets/new-round.png";
import { useHistory } from "react-router-dom";
import { TournamentRoundStage } from "../../graphql/generated";
import eventBg from "../Area/TablesList/assets/event-bg.png";
import {
  TournamentRoundStartUpdateSubscriptionSubscription,
  TournamentRoundStartUpdateSubscriptionSubscriptionVariables,
} from "../../graphql/TournamentRoundStartUpdateSubscription.generated";
import { useLocation } from "react-router";

const TOURNAMENT_ROUND_START_SUBSCRIPTION = loader(
  "../../graphql/TournamentRoundStartUpdateSubscription.graphql"
);

type RoundStart = NonNullable<
  TournamentRoundStartUpdateSubscriptionSubscription["roundStart"]
>;

type Message =
  | {
      type: "ADD_ROUND_START";
      payload: RoundStart;
    }
  | {
      type: "CLEAR_EXPIRED";
    }
  | {
      type: "POP";
    }
  | {
      type: "CLEAR";
    };

const reducer = produce((state: Array<RoundStart>, message: Message) => {
  switch (message.type) {
    case "ADD_ROUND_START":
      state.push(message.payload);
      return state;
    case "POP":
      return state.slice(1);
    case "CLEAR":
      state = [];
      return state;
    default:
      return state;
  }
});

const CloseButton = ({ closeHandler }: { closeHandler: () => void }) => {
  return (
    <div className="flex justify-center absolute right-0 top-0">
      <ButtonWithSound className="btn btn-lg p-0" onClick={closeHandler}>
        <img src={closeIcon} alt="close" />
      </ButtonWithSound>
    </div>
  );
};

const RoundData = ({
  roundData,
  onCancel,
}: {
  roundData: RoundStart;
  onCancel: (id: string) => void;
}) => {
  const { t } = useTranslation();
  const { push } = useHistory();

  const currentRound = React.useMemo(() => {
    switch (roundData?.roundStage) {
      case TournamentRoundStage.RoundOf_64:
        return `${t("instant_tournament.round_of")} 64`;
      case TournamentRoundStage.RoundOf_32:
        return `${t("instant_tournament.round_of")} 32`;
      case TournamentRoundStage.RoundOf_16:
        return `${t("instant_tournament.round_of")} 16`;
      case TournamentRoundStage.QuarterFinal:
        return t("instant_tournament.quarter_final");
      case TournamentRoundStage.SemiFinal:
        return t("instant_tournament.semi_final");
      case TournamentRoundStage.Final:
        return t("instant_tournament.final");
      default:
        return "";
    }
  }, [roundData?.roundStage]);

  const redirectToTournament = () => {
    onCancel(roundData?.tournamentId);
    push(
      `/area/${roundData?.roomId}:${roundData?.gameId}/${roundData?.tableId}`
    );
  };

  return (
    <div
      className={classNames(
        "flex flex-col relative bg-blue-800",
        styles.tournamentInvitation
      )}
    >
      <div className="flex justify-between items-center px-8 pt-3 pb-2 gap-8 absolute left-0 top-0 w-full">
        <div className="flex items-center gap-3 relative w-full">
          <div className={styles.eventPhoto}>
            <img src={newRoundIcon} alt="" width={220} height={120} />
          </div>
          <img src={eventBg} className={styles.invitationLogo} alt="img" />
          <CloseButton closeHandler={() => onCancel(roundData?.tournamentId)} />
        </div>
      </div>

      <div className="flex flex-col gap-8">
        <div className="flex flex-col px-8 gap-4 items-center text-center">
          <div className="flex flex-col gap-1 items-center text-center text-white text-base">
            <span className="font-semibold text-yellow-400 text-2xl">
              <Trans
                className="flex"
                i18nKey="instant_tournament.tournament_has_started"
                components={{
                  name: (
                    <span className="font-semibold text-yellow-400 text-2xl" />
                  ),
                }}
                values={{ round: currentRound }}
              />
            </span>
            <>
              <p>{t("instant_tournament.match_is_ready")}</p>
              <p>{t("instant_tournament.jump_back")}</p>
            </>
          </div>
        </div>
        <div className="flex justify-center gap-4">
          <ButtonWithSound
            onClick={redirectToTournament}
            className={classNames(
              "px-4",
              "text-white",
              "rounded-md",
              "font-semibold",
              "btn",
              "btn-green-600",
              "w-fit",
              "text-base"
            )}
          >
            {t("instant_tournament.play_round")}
          </ButtonWithSound>
        </div>
      </div>
    </div>
  );
};

const Popup = ({
  roundData,
  onCancel,
}: {
  roundData: RoundStart;
  onCancel: (id: string) => void;
}) => {
  return (
    <div className="absolute bg-black-alpha-30 top-0 left-0 right-0 bottom-0 z-50">
      <AbsoluteCenter>
        <RoundData roundData={roundData} onCancel={onCancel} />
      </AbsoluteCenter>
    </div>
  );
};

export const TournamentsStartRoundEvent = () => {
  const { pathname } = useLocation();

  const [{ data }] = useSubscription<
    unknown,
    TournamentRoundStartUpdateSubscriptionSubscription,
    TournamentRoundStartUpdateSubscriptionSubscriptionVariables
  >({
    query: TOURNAMENT_ROUND_START_SUBSCRIPTION,
    throwOnError: false,
  });

  const shouldIgnore = React.useMemo(() => {
    const gamePathRegex = /^\/area\/[^/]+:[^/]+\/(?!instant-tournaments|scheduled-tournaments|my-tournaments)[^/]+$/;

    const isGamePage = gamePathRegex.test(pathname);

    return (
      pathname.includes("/instant-tournaments/current") ||
      pathname.includes("/scheduled-tournaments/current") ||
      isGamePage
    );
  }, [pathname]);

  const [current, setCurrent] = React.useState<RoundStart | null>(null);
  const prevEventIdRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    const event = data?.roundStart;
    if (!event) return;

    if (prevEventIdRef.current === event.tournamentId) return;

    prevEventIdRef.current = event.tournamentId;

    if (shouldIgnore) {
      prevEventIdRef.current = null;
      return;
    }

    setCurrent(event);
  }, [data]);

  const onCancel = React.useCallback(() => {
    setCurrent(null);
    prevEventIdRef.current = null;
  }, []);

  return <>{current && <Popup roundData={current} onCancel={onCancel} />}</>;
};
